import React, { useState, useEffect } from 'react';
import { Switch } from '@/components/ui/switch';
import { Label } from '@/components/ui/label';
import { toast } from 'sonner';
import { Bell, Mail, MessageSquare, Smartphone } from 'lucide-react';
import apiClient from '@/utils/api';

const NotificationPreferencesSection = () => {
  const [prefs, setPrefs] = useState({ email: true, whatsapp: false, sms: false });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    const fetchPrefs = async () => {
      try {
        const res = await apiClient.get('/notifications/preferences');
        setPrefs(res.data.notification_preferences);
      } catch {
        // Use defaults
      } finally {
        setLoading(false);
      }
    };
    fetchPrefs();
  }, []);

  const handleToggle = async (channel, value) => {
    setSaving(true);
    const prev = { ...prefs };
    setPrefs(p => ({ ...p, [channel]: value }));
    try {
      const res = await apiClient.post('/notifications/preferences', { [channel]: value });
      setPrefs(res.data.notification_preferences);
      toast.success('Preference updated');
    } catch {
      setPrefs(prev);
      toast.error('Failed to update preference');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="bg-white p-6" data-testid="notification-prefs-loading">
        <div className="flex items-center gap-3 mb-4">
          <Bell className="w-5 h-5 text-gray-600" />
          <h3 className="text-lg font-semibold">Notification Preferences</h3>
        </div>
        <div className="h-20 flex items-center justify-center text-gray-400 text-sm">Loading...</div>
      </div>
    );
  }

  return (
    <div className="bg-white p-6" data-testid="notification-preferences-section">
      <div className="flex items-center gap-3 mb-4">
        <Bell className="w-5 h-5 text-gray-600" />
        <h3 className="text-lg font-semibold">Notification Preferences</h3>
      </div>
      <p className="text-sm text-gray-500 mb-5">Choose how you'd like to receive notifications. Security alerts are always sent via email.</p>

      <div className="space-y-4 max-w-md">
        <div className="flex items-center justify-between py-2">
          <div className="flex items-center gap-3">
            <Mail className="w-4 h-4 text-gray-500" />
            <div>
              <Label className="text-sm font-medium">Email notifications</Label>
              <p className="text-xs text-gray-400">Password resets, security alerts, updates</p>
            </div>
          </div>
          <Switch
            checked={prefs.email}
            onCheckedChange={(val) => handleToggle('email', val)}
            disabled={saving}
            data-testid="notif-email-toggle"
          />
        </div>

        <div className="flex items-center justify-between py-2 border-t border-gray-100">
          <div className="flex items-center gap-3">
            <MessageSquare className="w-4 h-4 text-gray-500" />
            <div>
              <Label className="text-sm font-medium">WhatsApp notifications</Label>
              <p className="text-xs text-gray-400">Coming soon</p>
            </div>
          </div>
          <Switch
            checked={prefs.whatsapp}
            onCheckedChange={(val) => handleToggle('whatsapp', val)}
            disabled={saving}
            data-testid="notif-whatsapp-toggle"
          />
        </div>

        <div className="flex items-center justify-between py-2 border-t border-gray-100">
          <div className="flex items-center gap-3">
            <Smartphone className="w-4 h-4 text-gray-500" />
            <div>
              <Label className="text-sm font-medium">SMS notifications</Label>
              <p className="text-xs text-gray-400">Coming soon</p>
            </div>
          </div>
          <Switch
            checked={prefs.sms}
            onCheckedChange={(val) => handleToggle('sms', val)}
            disabled={saving}
            data-testid="notif-sms-toggle"
          />
        </div>
      </div>
    </div>
  );
};

export default NotificationPreferencesSection;
